import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx-js-style";
import { toast } from "react-toastify";
import "./reports.css";
import "../finance/finance.css";
import { fmt } from "../finance/finance";

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthAgoISO = () => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
};
const VIEWS = [
    { key: "list", label: "Список" },
    { key: "tours", label: "По турам" },
    { key: "clients", label: "По заказчикам" },
    { key: "guides", label: "По гидам" },
    { key: "vehicles", label: "По машинам" },
    { key: "cafe", label: "По кафе" },
    { key: "operators", label: "По операторам" },
    { key: "drivers", label: "По водителям" },
    { key: "date", label: "По дате" },
];

const PAYMENT_LABELS = {
    cash: "Наличные",
    transfer: "Перечисление",
    terminal: "Терминал",
    debt: "Долг",
};

// 3 реальных типа заказа — по трём таблицам в базе (requests_individual/sharetour/partners)
const ORDER_TYPE_LABELS = {
    individual: "Индивидуальный",
    sharetour: "Share Tour",
    partners: "Партнёр",
};
const ORDER_TYPE_OPTIONS = ["Все", "Индивидуальный", "Share Tour", "Партнёр"];
const ORDER_TYPE_TO_KEY = { "Индивидуальный": "individual", "Share Tour": "sharetour", "Партнёр": "partners" };

// ключ статуса ('paid'/'partial'/'unpaid') — используется как имя CSS-класса,
// та же схема, что и в DebtorsList (finance.js): fn-pill paid/partial/unpaid
function statusPillClass(statusKey) {
    return statusKey || "unpaid";
}

function uniqueSorted(arr) {
    return Array.from(new Set(arr.filter((v) => v && v !== "нет" && v !== "—"))).sort();
}

const Breakdown = ({ title, entries, onPick, unit = "$" }) => (
    <div className="rep-extra">
        <div className="fn-section-label">{title}</div>
        {entries.length ? (
            entries.map(([key, d]) => (
                <div key={key} className="rep-row" onClick={() => onPick(key)}>
                    <span>
                        {key}
                        <span className="rep-count">×{d.count}</span>
                    </span>
                    <span className="rep-sum">
                        {unit === "$" ? `$${fmt(d.sum)}` : `${fmt(d.sum)} UZS`}
                    </span>
                </div>
            ))
        ) : (
            <div className="fn-empty">Нет данных за период</div>
        )}
    </div>
);

// Одно поле в блоке деталей заказа: подпись сверху, значение снизу — как vdField2 в макете
const VdField = ({ label, value }) => (
    <div className="rep-vd-field">
        <label>{label}</label>
        <div className="rep-vd-val">{value === null || value === undefined || value === "" ? "—" : value}</div>
    </div>
);

// Строка "имя — сумма — статус оплаты" внутри блока деталей (водитель/гид/кафе) — только для просмотра
const PersonLine = ({ name, extra, salary }) => (
    <div className="rep-vd-person">
        <span>
            {name}
            {extra ? <span className="rep-count">{extra}</span> : null}
        </span>
        <span className="rep-vd-person-right">
            {salary ? (
                <>
                    <span className="rep-sum">{fmt(salary)} UZS</span>
                    <span className="fn-pill paid">Оплачено</span>
                </>
            ) : (
                <span className="rep-sum" style={{ color: "var(--fn-text-soft)" }}>ещё не оплачено</span>
            )}
        </span>
    </div>
);

export const OrderDetailModal = ({ order, onClose }) => {
    if (!order) return null;
    const o = order;

    const driverTotal = (o.drivers || []).reduce((s, p) => s + (p.salary || 0), 0);
    const guideTotal = (o.guides || []).reduce((s, p) => s + (p.salary || 0), 0);
    const cafeTotal = (o.cafeAssignments || []).reduce((s, p) => s + (p.salary || 0), 0);
    const fuelTotal = (o.fuel || []).reduce((s, f) => s + (f.sum || 0), 0);
    const grandTotalUZS = driverTotal + guideTotal + cafeTotal + fuelTotal;

    return (
        <div className="rep-modal-overlay" onClick={onClose}>
            <div className="rep-modal-card" onClick={(e) => e.stopPropagation()}>
                <div className="rep-modal-head">
                    <div className="rep-modal-title">Заказ №{o.id} — полная хронология тура</div>
                    <button className="rep-modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="rep-modal-body">

                    <div className="rep-vd-section">
                        <h4>Основное</h4>
                        <div className="rep-vd-grid">
                            <VdField label="Оператор" value={o.operator} />
                            <VdField label="Заказчик" value={`${o.client} (${ORDER_TYPE_LABELS[o.requestType] || o.requestType})`} />
                            <VdField label="Маршрут" value={o.route} />
                            <VdField label="Дата" value={o.displayDate} />
                            <VdField label="Сумма заказа" value={`$${fmt(o.sum)}`} />
                            <VdField label="Тип оплаты" value={PAYMENT_LABELS[o.payment] || o.payment} />
                        </div>
                    </div>

                    <div className="rep-vd-section">
                        <h4>Транспорт{o.vehicle && o.vehicle !== "—" ? ` — ${o.transport}` : ""}</h4>
                        {(o.drivers || []).length ? (
                            (o.drivers || []).map((p) => (
                                <PersonLine
                                    key={p.name}
                                    name={p.name}
                                    extra={p.vehicle ? ` · ${p.vehicle}` : ""}
                                    salary={p.salary}
                                />
                            ))
                        ) : (
                            <div className="fn-hint">Водитель на этом заказе не назначен.</div>
                        )}
                    </div>

                    <div className="rep-vd-section">
                        <h4>Гиды</h4>
                        {(o.guides || []).length ? (
                            (o.guides || []).map((p) => (
                                <PersonLine
                                    key={p.name}
                                    name={p.name}
                                    salary={p.salary}
                                />
                            ))
                        ) : (
                            <div className="fn-hint">Гид на этом заказе не привлекался.</div>
                        )}
                    </div>

                    <div className="rep-vd-section">
                        <h4>Кафе / питание</h4>
                        {(o.cafeAssignments || []).length ? (
                            (o.cafeAssignments || []).map((p) => (
                                <PersonLine
                                    key={p.name}
                                    name={p.name}
                                    extra={` · ${p.count} чел.`}
                                    salary={p.salary}
                                />
                            ))
                        ) : (
                            <div className="fn-hint">Питание по этому заказу не организовывалось.</div>
                        )}
                    </div>
                    <div className="rep-vd-section">
                        <h4>Заправка</h4>
                        {(o.fuel || []).length ? (
                            (o.fuel || []).map((f, i) => (
                                <div className="rep-vd-person" key={"fuel-" + i}>
                                    <span>
                                        {f.plate}
                                        <span className="rep-count"> · {f.liters} л · {f.date}{f.driver ? ` · ${f.driver}` : ""}</span>
                                    </span>
                                    <span className="rep-sum">{fmt(f.sum)} UZS</span>
                                </div>
                            ))
                        ) : (
                            <div className="fn-hint">Заправка по этому заказу не привязана.</div>
                        )}
                    </div>
                    <div className="rep-vd-section">
                        <h4>Итого расходов по туру</h4>
                        <table className="rep-table">
                            <tbody>
                                {(o.drivers || []).map((p) => (
                                    <tr key={"d-" + p.name}>
                                        <td>Зарплата водителя ({p.name})</td>
                                        <td className="mono" style={{ textAlign: "right" }}>{p.salary != null ? fmt(p.salary) : "—"}</td>
                                    </tr>
                                ))}
                                {(o.guides || []).map((p) => (
                                    <tr key={"g-" + p.name}>
                                        <td>Зарплата гида ({p.name})</td>
                                        <td className="mono" style={{ textAlign: "right" }}>{p.salary != null ? fmt(p.salary) : "—"}</td>
                                    </tr>
                                ))}
                                {(o.cafeAssignments || []).map((p) => (
                                    <tr key={"c-" + p.name}>
                                        <td>Питание ({p.name}, {p.count} чел.)</td>
                                        <td className="mono" style={{ textAlign: "right" }}>{p.salary != null ? fmt(p.salary) : "—"}</td>
                                    </tr>
                                ))}
                                {(o.fuel || []).map((f, i) => (
                                    <tr key={"f-" + i}>
                                        <td>Заправка ({f.plate}, {f.date})</td>
                                        <td className="mono" style={{ textAlign: "right" }}>{fmt(f.sum)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="rep-summary-bar">
                            <span>Итого расходов по туру</span>
                            <span className="mono">{fmt(grandTotalUZS)} UZS</span>
                        </div>
                    </div>

                    <div className="rep-vd-section">
                        <h4>Оплата</h4>
                        <div className="rep-vd-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
                            <VdField label="Способ оплаты" value={PAYMENT_LABELS[o.payment] || o.payment} />
                            <div className="rep-vd-field">
                                <label>Статус</label>
                                <span className={`fn-pill ${statusPillClass(o.statusKey)}`}>{o.status}</span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};
const ROLE_FILTER_OPTIONS = [
    { value: "Все", label: "Все" },
    { value: "Гид", label: "Гид" },
    { value: "Водитель", label: "Водитель" },
    { value: "Кафе", label: "Кафе" },
    { value: "Авто", label: "Авто" },
    { value: "__none", label: "Без роли" },
];
const MiscReport = ({ API_URL, registerDownload }) => {
    const [dateFrom, setDateFrom] = useState(monthAgoISO());
    const [dateTo, setDateTo] = useState(todayISO());
    const [categories, setCategories] = useState([]);
    const [uncategorized, setUncategorized] = useState({ totals: {}, roles: [] });
    const [income, setIncome] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openTop, setOpenTop] = useState({});
    const [openSub, setOpenSub] = useState({});
    const [openRole, setOpenRole] = useState({});

    const [search, setSearch] = useState("");
    const [catFilter, setCatFilter] = useState("Все");
    const [subFilter, setSubFilter] = useState("Все");
    const [roleFilter, setRoleFilter] = useState("Все");
    const [nameFilter, setNameFilter] = useState("Все");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/getCategoryReport`, { dateFrom, dateTo });
            if (data.status === "success") {
                setCategories(data.categories || []);
                setUncategorized(data.uncategorized || { totals: {}, roles: [] });
                setIncome(data.income || []);
            } else {
                toast.error("Не удалось загрузить отчёт");
            }
        } catch (err) {
            console.error("getCategoryReport:", err);
            toast.error("Ошибка соединения с сервером");
        } finally {
            setLoading(false);
        }
    }, [API_URL, dateFrom, dateTo]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setSubFilter("Все"); }, [catFilter]);
    useEffect(() => { setRoleFilter("Все"); }, [catFilter, subFilter]);
    useEffect(() => { setNameFilter("Все"); }, [catFilter, subFilter, roleFilter]);

    const fmtTotals = (totals) =>
        Object.entries(totals || {})
            .map(([cur, val]) => (cur === "USD" ? `$${fmt(val)}` : `${fmt(val)} ${cur}`))
            .join(" · ") || "—";

    const s = search.trim().toLowerCase();

    // ── источник данных до фильтра по роли/имени — категория+подкатегория уже применены ──
    const scopedBuckets = useMemo(() => {
        const buckets = [];
        categories
            .filter((top) => catFilter === "Все" || catFilter === top.name)
            .forEach((top) => {
                if (subFilter === "Все" || subFilter === "__direct") buckets.push(top.direct);
                top.subcategories
                    .filter((sub) => subFilter === "Все" || subFilter === sub.name)
                    .forEach((sub) => buckets.push(sub));
            });
        if ((catFilter === "Все" || catFilter === "Без категории") && (subFilter === "Все" || subFilter === "__direct")) {
            buckets.push(uncategorized);
        }
        return buckets;
    }, [categories, uncategorized, catFilter, subFilter]);

    const catOptions = useMemo(
        () => ["Все", ...categories.map((c) => c.name), ...(uncategorized.roles.length ? ["Без категории"] : [])],
        [categories, uncategorized]
    );

    const subOptions = useMemo(() => {
        const opts = [];
        const seen = new Set();
        categories
            .filter((top) => catFilter === "Все" || catFilter === top.name)
            .forEach((top) => {
                if (top.direct.roles.length > 0 && !seen.has("__direct")) { opts.push({ value: "__direct", label: "Без подкатегории" }); seen.add("__direct"); }
                top.subcategories.forEach((sub) => {
                    if (!seen.has(sub.name)) { opts.push({ value: sub.name, label: sub.name }); seen.add(sub.name); }
                });
            });
        return [{ value: "Все", label: "Все" }, ...opts];
    }, [categories, catFilter]);

    // ── Роль — зависит от Категории/Подкатегории ──
    const roleOptions = useMemo(() => {
        const seen = new Set();
        scopedBuckets.forEach((b) => b.roles.forEach((r) => seen.add(r.role)));
        return ["Все", ...Array.from(seen).sort()];
    }, [scopedBuckets]);

    // ── Имя — зависит от Категории/Подкатегории/Роли ──
    const nameOptions = useMemo(() => {
        const seen = new Set();
        scopedBuckets.forEach((b) =>
            b.roles
                .filter((r) => roleFilter === "Все" || r.role === roleFilter)
                .forEach((r) => r.items.forEach((it) => seen.add(it.key)))
        );
        return ["Все", ...Array.from(seen).sort((a, b) => a.localeCompare(b, "ru"))];
    }, [scopedBuckets, roleFilter]);

    const itemMatches = (it) => {
        if (nameFilter !== "Все" && it.key !== nameFilter) return false;
        if (s && !it.key.toLowerCase().includes(s)) return false;
        return true;
    };

    const filterBucket = (bucket) => {
        const roles = bucket.roles
            .filter((r) => roleFilter === "Все" || r.role === roleFilter)
            .map((r) => ({ ...r, items: r.items.filter(itemMatches) }))
            .filter((r) => r.items.length > 0);
        return { totals: bucket.totals, roles };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    };

    const filteredCategories = useMemo(() => {
        return categories
            .filter((top) => catFilter === "Все" || catFilter === top.name)
            .map((top) => {
                const showDirect = subFilter === "Все" || subFilter === "__direct";
                const direct = showDirect ? filterBucket(top.direct) : { totals: {}, roles: [] };
                const subs = top.subcategories
                    .filter((sub) => subFilter === "Все" || subFilter === sub.name)
                    .map((sub) => ({ ...sub, ...filterBucket(sub) }))
                    .filter((sub) => sub.roles.length > 0);
                return { ...top, direct, subcategories: subs };
            })
            .filter((top) => top.direct.roles.length > 0 || top.subcategories.length > 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categories, catFilter, subFilter, roleFilter, nameFilter, s]);

    const filteredUncategorized = useMemo(() => {
        if (catFilter !== "Все" && catFilter !== "Без категории") return { totals: {}, roles: [] };
        if (subFilter !== "Все" && subFilter !== "__direct") return { totals: {}, roles: [] };
        return filterBucket(uncategorized);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uncategorized, catFilter, subFilter, roleFilter, nameFilter, s]);

    const filteredIncome = useMemo(() => {
        if (catFilter !== "Все") return [];
        if (!s) return income;
        return income.filter((r) => (r.comment || "").toLowerCase().includes(s) || (r.payment || "").toLowerCase().includes(s));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [income, catFilter, s]);

    const incomeTotals = filteredIncome.reduce((acc, r) => {
        acc[r.currency] = (acc[r.currency] || 0) + r.amount;
        return acc;
    }, {});
    const expenseTotals = useMemo(() => {
        const totals = {};
        const addRoles = (roles) => {
            roles.forEach((r) => {
                Object.entries(r.totals).forEach(([cur, val]) => {
                    totals[cur] = (totals[cur] || 0) + val;
                });
            });
        };
        filteredCategories.forEach((top) => {
            addRoles(top.direct.roles);
            top.subcategories.forEach((sub) => addRoles(sub.roles));
        });
        addRoles(filteredUncategorized.roles);
        return totals;
    }, [filteredCategories, filteredUncategorized]);

    const allCurrencies = useMemo(
        () => Array.from(new Set([...Object.keys(incomeTotals), ...Object.keys(expenseTotals)])).sort(),
        [incomeTotals, expenseTotals]
    );
    const downloadExcel = useCallback(() => {
        const hasExpenses = filteredCategories.length > 0 || filteredUncategorized.roles.length > 0;
        if (!hasExpenses && filteredIncome.length === 0) {
            toast.info("Нет данных для выгрузки по текущим фильтрам");
            return;
        }

        const headerStyle = (fillColor) => ({
            fill: { fgColor: { rgb: fillColor } },
            font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
            alignment: { vertical: "center", horizontal: "center", wrapText: true },
            border: {
                top: { style: "thin", color: { rgb: "D0D3DA" } },
                bottom: { style: "thin", color: { rgb: "D0D3DA" } },
                left: { style: "thin", color: { rgb: "D0D3DA" } },
                right: { style: "thin", color: { rgb: "D0D3DA" } },
            },
        });
        const bodyBorder = {
            border: {
                top: { style: "thin", color: { rgb: "EDEEF2" } },
                bottom: { style: "thin", color: { rgb: "EDEEF2" } },
                left: { style: "thin", color: { rgb: "EDEEF2" } },
                right: { style: "thin", color: { rgb: "EDEEF2" } },
            },
        };
        const zebra = (rowIdx) => (rowIdx % 2 === 1 ? { fill: { fgColor: { rgb: "F7F8FA" } } } : {});

        const buildSheet = (rows, headers, fillColor, moneyCols = {}, boldLastRow = false) => {
            const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
            const range = XLSX.utils.decode_range(ws["!ref"]);

            for (let c = range.s.c; c <= range.e.c; c++) {
                const headCell = XLSX.utils.encode_cell({ r: 0, c });
                if (ws[headCell]) ws[headCell].s = headerStyle(fillColor);

                for (let r = 1; r <= range.e.r; r++) {
                    const cellRef = XLSX.utils.encode_cell({ r, c });
                    if (!ws[cellRef]) continue;
                    const colName = headers[c];
                    const isLastRow = boldLastRow && r === range.e.r;
                    const style = { ...bodyBorder, ...(isLastRow ? {} : zebra(r)) };
                    if (isLastRow) {
                        style.font = { bold: true };
                        style.fill = { fgColor: { rgb: "EFEFEF" } };
                    }
                    if (moneyCols[colName]) {
                        style.numFmt = moneyCols[colName];
                        style.alignment = { horizontal: "right" };
                    }
                    ws[cellRef].s = style;
                }
            }
            ws["!cols"] = headers.map((h) => ({ wch: Math.max(12, h.length + 4) }));
            ws["!freeze"] = { xSplit: 0, ySplit: 1 };
            return ws;
        };

        const wb = XLSX.utils.book_new();

        // ── Лист 1: Итоги — период + приход/расход/прибыль по каждой валюте ──
        const summaryHeaders = ["Валюта", "Приход", "Расход", "Прибыль / Убыток"];
        const summaryRows = allCurrencies.map((cur) => {
            const inc = incomeTotals[cur] || 0;
            const exp = expenseTotals[cur] || 0;
            return { "Валюта": cur, "Приход": inc, "Расход": exp, "Прибыль / Убыток": inc - exp };
        });
        if (summaryRows.length) {
            const summarySheet = buildSheet(summaryRows, summaryHeaders, "341F47", {
                "Приход": '#,##0.00', "Расход": '#,##0.00', "Прибыль / Убыток": '#,##0.00',
            });
            // строка периода сверху, над таблицей
            XLSX.utils.sheet_add_aoa(summarySheet, [[`Период: ${dateFrom} — ${dateTo}`]], { origin: -1 });
            XLSX.utils.book_append_sheet(wb, summarySheet, "Итоги");
        }

        // ── Лист 2: Расходы по категориям — с итоговой строкой по каждой валюте ──
        const expenseHeaders = ["Категория", "Подкатегория", "Роль", "Кому / название", "Валюта", "Сумма"];
        const expenseRows = [];
        const pushRoleItems = (roleBucket, catName, subName) => {
            roleBucket.items.forEach((it) => {
                Object.entries(it.totals).forEach(([cur, val]) => {
                    expenseRows.push({ "Категория": catName, "Подкатегория": subName, "Роль": roleBucket.role, "Кому / название": it.key, "Валюта": cur, "Сумма": val });
                });
            });
        };
        filteredCategories.forEach((top) => {
            top.direct.roles.forEach((r) => pushRoleItems(r, top.name, "—"));
            top.subcategories.forEach((sub) => {
                sub.roles.forEach((r) => pushRoleItems(r, top.name, sub.name));
            });
        });
        filteredUncategorized.roles.forEach((r) => pushRoleItems(r, "Без категории", "—"));
        if (expenseRows.length) {
            Object.entries(expenseTotals).forEach(([cur, val]) => {
                expenseRows.push({ "Категория": "", "Подкатегория": "", "Роль": "", "Кому / название": `Итого ${cur}`, "Валюта": cur, "Сумма": val });
            });
            XLSX.utils.book_append_sheet(wb, buildSheet(expenseRows, expenseHeaders, "C23B3B", { "Сумма": '#,##0.00' }, true), "Расходы по категориям");
        }

        // ── Лист 3: Прочий приход — с итоговой строкой по каждой валюте ──
        const incomeHeaders = ["Дата", "Касса", "Комментарий", "Валюта", "Сумма"];
        const incomeRows = filteredIncome.map((r) => ({
            "Дата": new Date(r.date).toLocaleDateString("ru-RU"),
            "Касса": r.payment || "—",
            "Комментарий": r.comment || "—",
            "Валюта": r.currency,
            "Сумма": r.amount,
        }));
        if (incomeRows.length) {
            Object.entries(incomeTotals).forEach(([cur, val]) => {
                incomeRows.push({ "Дата": "", "Касса": "", "Комментарий": `Итого ${cur}`, "Валюта": cur, "Сумма": val });
            });
            XLSX.utils.book_append_sheet(wb, buildSheet(incomeRows, incomeHeaders, "1E9450", { "Сумма": '#,##0.00' }, true), "Прочий приход");
        }

        XLSX.writeFile(wb, `Прочие_отчеты_${dateFrom}_${dateTo}.xlsx`);
    }, [filteredCategories, filteredUncategorized, filteredIncome, incomeTotals, expenseTotals, allCurrencies, dateFrom, dateTo]);

    useEffect(() => { registerDownload && registerDownload(downloadExcel); }, [registerDownload, downloadExcel]);

    const renderRoleBlock = (r, roleKey) => (
        <div className="rep-cat-role" key={roleKey}>
            <div className="rep-cat-role-head" onClick={() => setOpenRole((p) => ({ ...p, [roleKey]: !p[roleKey] }))}>
                <span>
                    <i className={`fa-solid fa-chevron-${openRole[roleKey] ? "down" : "right"}`} style={{ marginRight: 8, fontSize: 9 }} />
                    {r.role}
                </span>
                <span className="rep-sum">{fmtTotals(r.totals)}</span>
            </div>
            {openRole[roleKey] && (
                <div className="rep-cat-items">
                    {r.items.map((it) => (
                        <div className="rep-row" key={it.key}>
                            <span>{it.key}</span>
                            <span className="rep-sum">{fmtTotals(it.totals)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <>


            <div className="rep-filters">
                <div className="rep-filters-row">
                    <div className="fn-field">
                        <label>Период — от</label>
                        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                    </div>
                    <div className="fn-field">
                        <label>Период — до</label>
                        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                    </div>
                    <div className="fn-field">
                        <label>Поиск</label>
                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Имя, название, комментарий..." />
                    </div>
                </div>
                <div className="rep-filters-row" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
                    <div className="fn-field">
                        <label>Категория</label>
                        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
                            {catOptions.map((c) => <option key={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="fn-field">
                        <label>Подкатегория</label>
                        <select value={subFilter} onChange={(e) => setSubFilter(e.target.value)}>
                            {subOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div className="fn-field">
                        <label>Роль</label>
                        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                            {roleOptions.map((r) => <option key={r}>{r}</option>)}
                        </select>
                    </div>
                    <div className="fn-field">
                        <label>Имя</label>
                        <select value={nameFilter} onChange={(e) => setNameFilter(e.target.value)}>
                            {nameOptions.map((n) => <option key={n}>{n}</option>)}
                        </select>
                    </div>
                </div>
                {!loading && allCurrencies.length > 0 && (
                    <div className="rep-profit-summary">
                        {allCurrencies.map((cur) => {
                            const inc = incomeTotals[cur] || 0;
                            const exp = expenseTotals[cur] || 0;
                            const profit = inc - exp;
                            const fmtCur = (v) => (cur === "USD" ? `$${fmt(v)}` : `${fmt(v)} ${cur}`);
                            return (
                                <div className="rep-profit-card" key={cur}>
                                    <div className="rep-profit-cur">{cur}</div>
                                    <div className="rep-profit-row">
                                        <span className="rep-profit-lbl">Приход</span>
                                        <span className="rep-profit-val income">{fmtCur(inc)}</span>
                                    </div>
                                    <div className="rep-profit-row">
                                        <span className="rep-profit-lbl">Расход</span>
                                        <span className="rep-profit-val expense">{fmtCur(exp)}</span>
                                    </div>
                                    <div className="rep-profit-row rep-profit-row--total">
                                        <span className="rep-profit-lbl">{profit >= 0 ? "Прибыль" : "Убыток"}</span>
                                        <span className={`rep-profit-val ${profit >= 0 ? "income" : "expense"}`}>{fmtCur(Math.abs(profit))}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {loading ? (
                <div className="fn-empty">Загрузка…</div>
            ) : (
                <>
                    <div className="fn-section-label">Расходы по категориям</div>

                    {Object.keys(expenseTotals).length > 0 && (
                        <div className="fn-stats" style={{ display: "flex", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
                            {Object.entries(expenseTotals).map(([cur, val]) => (
                                <div className="fn-stat debt" key={cur}>
                                    <div className="fn-lbl">Итого {cur}</div>
                                    <div className="fn-val">{cur === "USD" ? `$${fmt(val)}` : `${fmt(val)} ${cur}`}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    {filteredCategories.length === 0 && filteredUncategorized.roles.length === 0 ? (
                        <div className="fn-empty">Ничего не найдено по этим фильтрам</div>
                    ) : (
                        <div className="rep-cat-tree">
                            {filteredCategories.map((top) => (
                                <div className="rep-cat-top" key={top.id}>
                                    <div
                                        className="rep-cat-top-head"
                                        onClick={() => setOpenTop((p) => ({ ...p, [top.id]: !p[top.id] }))}
                                    >
                                        <span>
                                            <i className={`fa-solid fa-chevron-${openTop[top.id] ? "down" : "right"}`} style={{ marginRight: 8, fontSize: 11 }} />
                                            {top.name}
                                        </span>
                                        <span className="rep-sum">{fmtTotals(top.totals)}</span>
                                    </div>
                                    {openTop[top.id] && (
                                        <div className="rep-cat-subs">
                                            {top.direct.roles.map((r) => {
                                                const roleKey = `${top.id}-direct-${r.role}`;
                                                return (
                                                    <div className="rep-cat-sub" key={roleKey}>
                                                        <div className="rep-cat-sub-head" onClick={() => setOpenSub((p) => ({ ...p, [roleKey]: !p[roleKey] }))}>
                                                            <span>
                                                                <i className={`fa-solid fa-chevron-${openSub[roleKey] ? "down" : "right"}`} style={{ marginRight: 8, fontSize: 10 }} />
                                                                {r.role}
                                                            </span>
                                                            <span className="rep-sum">{fmtTotals(r.totals)}</span>
                                                        </div>
                                                        {openSub[roleKey] && (
                                                            <div className="rep-cat-items">
                                                                {r.items.map((it) => (
                                                                    <div className="rep-row" key={it.key}>
                                                                        <span>{it.key}</span>
                                                                        <span className="rep-sum">{fmtTotals(it.totals)}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}

                                            {top.subcategories.map((sub) => {
                                                const subKey = `${top.id}-${sub.id}`;
                                                return (
                                                    <div className="rep-cat-sub" key={subKey}>
                                                        <div
                                                            className="rep-cat-sub-head"
                                                            onClick={() => setOpenSub((p) => ({ ...p, [subKey]: !p[subKey] }))}
                                                        >
                                                            <span>
                                                                <i className={`fa-solid fa-chevron-${openSub[subKey] ? "down" : "right"}`} style={{ marginRight: 8, fontSize: 10 }} />
                                                                {sub.name}
                                                                {sub.listSource && <span className="kz-cat-list-tag" style={{ marginLeft: 8 }}>список</span>}
                                                            </span>
                                                            <span className="rep-sum">{fmtTotals(sub.totals)}</span>
                                                        </div>
                                                        {openSub[subKey] && (
                                                            <div className="rep-cat-roles">
                                                                {sub.roles.map((r) => renderRoleBlock(r, `${subKey}-${r.role}`))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            ))}

                            {filteredUncategorized.roles.length > 0 && (
                                <div className="rep-cat-top">
                                    <div
                                        className="rep-cat-top-head"
                                        onClick={() => setOpenTop((p) => ({ ...p, uncategorized: !p.uncategorized }))}
                                    >
                                        <span>
                                            <i className={`fa-solid fa-chevron-${openTop.uncategorized ? "down" : "right"}`} style={{ marginRight: 8, fontSize: 11 }} />
                                            Без категории
                                        </span>
                                        <span className="rep-sum">{fmtTotals(filteredUncategorized.totals)}</span>
                                    </div>
                                    {openTop.uncategorized && (
                                        <div className="rep-cat-subs">
                                            {filteredUncategorized.roles.map((r) => {
                                                const roleKey = `uncategorized-${r.role}`;
                                                return (
                                                    <div className="rep-cat-sub" key={roleKey}>
                                                        <div className="rep-cat-sub-head" onClick={() => setOpenSub((p) => ({ ...p, [roleKey]: !p[roleKey] }))}>
                                                            <span>
                                                                <i className={`fa-solid fa-chevron-${openSub[roleKey] ? "down" : "right"}`} style={{ marginRight: 8, fontSize: 10 }} />
                                                                {r.role}
                                                            </span>
                                                            <span className="rep-sum">{fmtTotals(r.totals)}</span>
                                                        </div>
                                                        {openSub[roleKey] && (
                                                            <div className="rep-cat-items">
                                                                {r.items.map((it) => (
                                                                    <div className="rep-row" key={it.key}>
                                                                        <span>{it.key}</span>
                                                                        <span className="rep-sum">{fmtTotals(it.totals)}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="fn-section-label" style={{ marginTop: 28 }}>Прочий приход</div>
                    {filteredIncome.length === 0 ? (
                        <div className="fn-empty">
                            {catFilter !== "Все" ? "У прихода нет категорий — сбрось фильтр «Категория», чтобы его увидеть" : "Прочих приходов за период нет"}
                        </div>
                    ) : (
                        <>
                            <div className="fn-stats" style={{ display: "flex", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
                                {Object.entries(incomeTotals).map(([cur, val]) => (
                                    <div className="fn-stat paid" key={cur}>
                                        <div className="fn-lbl">Итого {cur}</div>
                                        <div className="fn-val">{cur === "USD" ? `$${fmt(val)}` : `${fmt(val)} ${cur}`}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="rep-table-wrap">
                                <table className="rep-table">
                                    <thead>
                                        <tr><th>Дата</th><th>Касса</th><th>Комментарий</th><th style={{ textAlign: "right" }}>Сумма</th></tr>
                                    </thead>
                                    <tbody>
                                        {filteredIncome.map((r) => (
                                            <tr key={r.id}>
                                                <td>{new Date(r.date).toLocaleDateString("ru-RU")}</td>
                                                <td>{r.payment || "—"}</td>
                                                <td>{r.comment || "—"}</td>
                                                <td className="mono" style={{ textAlign: "right" }}>
                                                    {r.currency === "USD" ? `$${fmt(r.amount)}` : `${fmt(r.amount)} ${r.currency}`}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </>
            )}
        </>
    );
};

const Reports = ({ API_URL }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("list");
    const [detailOrder, setDetailOrder] = useState(null);
    const [reportTab, setReportTab] = useState("master"); // 'master' | 'misc'
    const miscDownloadRef = useRef(null);
    const [dateFrom, setDateFrom] = useState(monthAgoISO());
    const [dateTo, setDateTo] = useState(todayISO());
    const [search, setSearch] = useState("");
    const [ctype, setCtype] = useState("Все");
    const [client, setClient] = useState("Все");
    const [tour, setTour] = useState("Все");
    const [operator, setOperator] = useState("Все");
    const [guide, setGuide] = useState("Все");
    const [driver, setDriver] = useState("Все");
    const [vehicle, setVehicle] = useState("Все");
    const [cafe, setCafe] = useState("Все");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.post(`${API_URL}/getMasterReport`, { dateFrom, dateTo });
            if (res.data.status === "success") {
                setOrders(res.data.orders);
            } else {
                toast.error("Не удалось загрузить отчёт");
            }
        } catch (err) {
            console.error("getMasterReport:", err);
            toast.error("Ошибка соединения с сервером");
        } finally {
            setLoading(false);
        }
    }, [API_URL, dateFrom, dateTo]);

    useEffect(() => { load(); }, [load]);

    // если открыта модалка "Подробно" — держим её данные свежими после load() (например, после toggle оплаты)
    useEffect(() => {
        if (!detailOrder) return;
        const fresh = orders.find((o) => o.id === detailOrder.id);
        if (fresh) setDetailOrder(fresh);
    }, [orders]); // eslint-disable-line react-hooks/exhaustive-deps

    const s = search.trim().toLowerCase();

    // Проверка одного заказа по одному конкретному фильтру — вынесено отдельно,
    // чтобы можно было "выключать" любой из фильтров при построении списка вариантов
    // для соответствующего select'а (каскадность: например, при Тип заказа = Партнёр
    // список Заказчиков должен показывать только партнёров).
    const matchesFilter = (o, dim) => {
        if (dim !== "ctype" && ctype !== "Все" && o.requestType !== ORDER_TYPE_TO_KEY[ctype]) return false;
        if (dim !== "client" && client !== "Все" && o.client !== client) return false;
        if (dim !== "tour" && tour !== "Все" && o.route !== tour) return false;
        if (dim !== "operator" && operator !== "Все" && o.operator !== operator) return false;
        if (dim !== "guide" && guide !== "Все" && !(o.guideNames || [o.guide]).includes(guide)) return false;
        if (dim !== "driver" && driver !== "Все" && !(o.driverNames || [o.driver]).includes(driver)) return false;
        if (dim !== "vehicle" && vehicle !== "Все" && !(o.vehicleNames || [o.vehicle]).includes(vehicle)) return false;
        if (dim !== "cafe" && cafe !== "Все" && !(o.cafeNames || [o.cafe]).includes(cafe)) return false;
        if (s) {
            const hay = [o.client, o.route, o.driver, o.guide, o.operator, o.vehicle, o.cafe, o.id]
                .join(" ")
                .toLowerCase();
            if (!hay.includes(s)) return false;
        }
        return true;
    };
    const matchesAll = (o) => matchesFilter(o, null);

    const filtered = useMemo(
        () => orders.filter(matchesAll),
        [orders, ctype, client, tour, operator, guide, driver, vehicle, cafe, search]
    );

    // Опции для каждого select'а — только то, что реально встречается среди заказов,
    // уже отфильтрованных ВСЕМИ ОСТАЛЬНЫМИ фильтрами (кроме самого этого измерения).
    const options = useMemo(
        () => ({
            clients: uniqueSorted(orders.filter((o) => matchesFilter(o, "client")).map((o) => o.client)),
            tours: uniqueSorted(orders.filter((o) => matchesFilter(o, "tour")).map((o) => o.route)),
            operators: uniqueSorted(orders.filter((o) => matchesFilter(o, "operator")).map((o) => o.operator)),
            guides: uniqueSorted(orders.filter((o) => matchesFilter(o, "guide")).flatMap((o) => o.guideNames || [o.guide])),
            drivers: uniqueSorted(orders.filter((o) => matchesFilter(o, "driver")).flatMap((o) => o.driverNames || [o.driver])),
            vehicles: uniqueSorted(orders.filter((o) => matchesFilter(o, "vehicle")).flatMap((o) => o.vehicleNames || [o.vehicle])),
            cafes: uniqueSorted(orders.filter((o) => matchesFilter(o, "cafe")).flatMap((o) => o.cafeNames || [o.cafe])),
        }),
        [orders, ctype, client, tour, operator, guide, driver, vehicle, cafe, search]
    );

    // если выбранное значение фильтра больше не входит в свой же (сузившийся) список вариантов —
    // сбрасываем обратно на "Все", чтобы не оставалось "залипшего" невидимого выбора
    useEffect(() => { if (client !== "Все" && !options.clients.includes(client)) setClient("Все"); }, [options.clients]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (tour !== "Все" && !options.tours.includes(tour)) setTour("Все"); }, [options.tours]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (operator !== "Все" && !options.operators.includes(operator)) setOperator("Все"); }, [options.operators]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (guide !== "Все" && !options.guides.includes(guide)) setGuide("Все"); }, [options.guides]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (driver !== "Все" && !options.drivers.includes(driver)) setDriver("Все"); }, [options.drivers]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (vehicle !== "Все" && !options.vehicles.includes(vehicle)) setVehicle("Все"); }, [options.vehicles]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (cafe !== "Все" && !options.cafes.includes(cafe)) setCafe("Все"); }, [options.cafes]); // eslint-disable-line react-hooks/exhaustive-deps

    const total = filtered.reduce((s, o) => s + o.sum, 0);
    const paidCount = filtered.filter((o) => o.statusKey === "paid").length;

    const groupBy = (rows, keyFn) => {
        const map = {};
        rows.forEach((o) => {
            const key = keyFn(o);
            if (!map[key]) map[key] = { count: 0, sum: 0 };
            map[key].count++;
            map[key].sum += o.sum;
        });
        return map;
    };
    const sortedEntries = (map) => Object.entries(map).sort((a, b) => b[1].sum - a[1].sum);

    const downloadExcel = () => {
        if (!filtered.length) {
            toast.info("Нет данных для выгрузки по текущим фильтрам");
            return;
        }

        // ── стили ──
        const headerStyle = (fillColor) => ({
            fill: { fgColor: { rgb: fillColor } },
            font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
            alignment: { vertical: "center", horizontal: "center", wrapText: true },
            border: {
                top: { style: "thin", color: { rgb: "D0D3DA" } },
                bottom: { style: "thin", color: { rgb: "D0D3DA" } },
                left: { style: "thin", color: { rgb: "D0D3DA" } },
                right: { style: "thin", color: { rgb: "D0D3DA" } },
            },
        });
        const bodyBorder = {
            border: {
                top: { style: "thin", color: { rgb: "EDEEF2" } },
                bottom: { style: "thin", color: { rgb: "EDEEF2" } },
                left: { style: "thin", color: { rgb: "EDEEF2" } },
                right: { style: "thin", color: { rgb: "EDEEF2" } },
            },
        };
        const zebra = (rowIdx) => (rowIdx % 2 === 1 ? { fill: { fgColor: { rgb: "F7F8FA" } } } : {});

        // Строит лист из массива объектов, красит заголовок в fillColor, зебру и денежные форматы
        const buildSheet = (rows, headers, fillColor, moneyCols = {}) => {
            const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
            const range = XLSX.utils.decode_range(ws["!ref"]);

            for (let c = range.s.c; c <= range.e.c; c++) {
                const headCell = XLSX.utils.encode_cell({ r: 0, c });
                if (ws[headCell]) ws[headCell].s = headerStyle(fillColor);

                for (let r = 1; r <= range.e.r; r++) {
                    const cellRef = XLSX.utils.encode_cell({ r, c });
                    if (!ws[cellRef]) continue;
                    const colName = headers[c];
                    const style = { ...bodyBorder, ...zebra(r) };
                    if (moneyCols[colName]) {
                        style.numFmt = moneyCols[colName];
                        style.alignment = { horizontal: "right" };
                    }
                    ws[cellRef].s = style;
                }
            }
            ws["!cols"] = headers.map((h) => ({ wch: Math.max(12, h.length + 4) }));
            ws["!freeze"] = { xSplit: 0, ySplit: 1 };
            return ws;
        };

        const wb = XLSX.utils.book_new();

        // ── Лист 1: Список заказов ──
        const listHeaders = ["№ заказа", "Дата", "Оператор", "Маршрут", "Заказчик", "Тип заказа", "Транспорт", "Машина", "Водитель", "Гид", "Кафе", "Тип оплаты", "Сумма, $", "Статус"];
        const listRows = filtered.map((o) => ({
            "№ заказа": o.id,
            Дата: o.displayDate,
            Оператор: o.operator,
            Маршрут: o.route,
            Заказчик: o.client,
            "Тип заказа": ORDER_TYPE_LABELS[o.requestType] || o.requestType,
            Транспорт: o.transport,
            Машина: o.vehicle,
            Водитель: o.driver,
            Гид: o.guide,
            Кафе: o.cafe,
            "Тип оплаты": PAYMENT_LABELS[o.payment] || o.payment,
            "Сумма, $": o.sum,
            Статус: o.status,
        }));
        XLSX.utils.book_append_sheet(
            wb,
            buildSheet(listRows, listHeaders, "16213F", { "Сумма, $": '"$"#,##0' }),
            "Список"
        );

        // ── Лист 2: По турам — сумма заказов + расходы (зарплаты водителей/гидов/кафе) ──
        const tourMap = {};
        filtered.forEach((o) => {
            if (!tourMap[o.route]) tourMap[o.route] = { count: 0, revenue: 0, driverExp: 0, guideExp: 0, cafeExp: 0, fuelExp: 0 };
            tourMap[o.route].count++;
            tourMap[o.route].revenue += o.sum;
            tourMap[o.route].driverExp += (o.drivers || []).reduce((s, p) => s + (p.salary || 0), 0);
            tourMap[o.route].guideExp += (o.guides || []).reduce((s, p) => s + (p.salary || 0), 0);
            tourMap[o.route].cafeExp += (o.cafeAssignments || []).reduce((s, p) => s + (p.salary || 0), 0);
            tourMap[o.route].fuelExp += (o.fuel || []).reduce((s, f) => s + (f.sum || 0), 0);
        });
        const tourHeaders = ["Тур", "Заказов", "Выручка, $", "Зарплаты водителей, UZS", "Зарплаты гидов, UZS", "Питание, UZS", "Заправка, UZS", "Всего расходов, UZS"];
        const tourRows = Object.entries(tourMap)
            .sort((a, b) => b[1].revenue - a[1].revenue)
            .map(([route, d]) => ({
                "Тур": route,
                "Заказов": d.count,
                "Выручка, $": d.revenue,
                "Зарплаты водителей, UZS": d.driverExp,
                "Зарплаты гидов, UZS": d.guideExp,
                "Питание, UZS": d.cafeExp,
                "Заправка, UZS": d.fuelExp,
                "Всего расходов, UZS": d.driverExp + d.guideExp + d.cafeExp + d.fuelExp,
            }));
        XLSX.utils.book_append_sheet(
            wb,
            buildSheet(tourRows, tourHeaders, "2FB871", {
                "Выручка, $": '"$"#,##0',
                "Зарплаты водителей, UZS": '#,##0" UZS"',
                "Зарплаты гидов, UZS": '#,##0" UZS"',
                "Питание, UZS": '#,##0" UZS"',
                "Заправка, UZS": '#,##0" UZS"',
                "Всего расходов, UZS": '#,##0" UZS"',
            }),
            "По турам"
        );

        // ── Лист 3: По гидам ──
        const guideMap = {};
        filtered.forEach((o) => {
            (o.guides || []).forEach((p) => {
                if (!guideMap[p.name]) guideMap[p.name] = { trips: 0, salary: 0 };
                guideMap[p.name].trips++;
                guideMap[p.name].salary += p.salary || 0;
            });
        });
        const guideHeaders = ["Гид", "Выездов", "Выплачено, UZS"];
        const guideRows = Object.entries(guideMap)
            .sort((a, b) => b[1].salary - a[1].salary)
            .map(([name, d]) => ({ "Гид": name, "Выездов": d.trips, "Выплачено, UZS": d.salary }));
        XLSX.utils.book_append_sheet(
            wb,
            buildSheet(guideRows, guideHeaders, "3E7BFA", { "Выплачено, UZS": '#,##0" UZS"' }),
            "По гидам"
        );

        // ── Лист 4: По водителям ──
        const driverMap = {};
        filtered.forEach((o) => {
            (o.drivers || []).forEach((p) => {
                if (!driverMap[p.name]) driverMap[p.name] = { trips: 0, salary: 0, vehicles: new Set() };
                driverMap[p.name].trips++;
                driverMap[p.name].salary += p.salary || 0;
                if (p.vehicle) driverMap[p.name].vehicles.add(p.vehicle);
            });
        });
        const driverHeaders = ["Водитель", "Машина", "Выездов", "Выплачено, UZS"];
        const driverRows = Object.entries(driverMap)
            .sort((a, b) => b[1].salary - a[1].salary)
            .map(([name, d]) => ({
                "Водитель": name,
                "Машина": Array.from(d.vehicles).join(", ") || "—",
                "Выездов": d.trips,
                "Выплачено, UZS": d.salary,
            }));
        XLSX.utils.book_append_sheet(
            wb,
            buildSheet(driverRows, driverHeaders, "FF7A4D", { "Выплачено, UZS": '#,##0" UZS"' }),
            "По водителям"
        );

        // ── Лист 5: По кафе ──
        const cafeMap = {};
        filtered.forEach((o) => {
            (o.cafeAssignments || []).forEach((a) => {
                if (!cafeMap[a.name]) cafeMap[a.name] = { visits: 0, people: 0, salary: 0 };
                cafeMap[a.name].visits++;
                cafeMap[a.name].people += a.count || 0;
                cafeMap[a.name].salary += a.salary || 0;
            });
        });
        const cafeHeaders = ["Кафе", "Заездов", "Человек всего", "Выплачено, UZS"];
        const cafeRows = Object.entries(cafeMap)
            .sort((a, b) => b[1].salary - a[1].salary)
            .map(([name, d]) => ({
                "Кафе": name,
                "Заездов": d.visits,
                "Человек всего": d.people,
                "Выплачено, UZS": d.salary,
            }));
        XLSX.utils.book_append_sheet(
            wb,
            buildSheet(cafeRows, cafeHeaders, "A9791E", { "Выплачено, UZS": '#,##0" UZS"' }),
            "По кафе"
        );

        XLSX.writeFile(wb, `Сводный_отчёт_${dateFrom}_${dateTo}.xlsx`);
    };

    const renderPersonHistory = (role, listField, selected) => {
        if (selected === "Все") return null;
        const rows = [];
        filtered.forEach((o) => {
            (o[listField] || []).forEach((p) => {
                if (p.name === selected) rows.push({ order: o, person: p });
            });
        });
        return (
            <div style={{ marginTop: 16, borderTop: "1px solid var(--fn-border)", paddingTop: 16 }}>
                <div className="fn-section-label">{selected} — история за период</div>
                <div className="rep-table-wrap">
                    <table className="rep-table">
                        <thead>
                            <tr>
                                <th>Дата</th><th>Тур</th><th>Заказчик</th>
                                <th style={{ textAlign: "right" }}>Зарплата, UZS</th><th>Выплата</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(({ order: o, person: p }) => (
                                <tr key={o.id + "|" + p.name}>
                                    <td>{o.displayDate}</td>
                                    <td>{o.route}</td>
                                    <td>{o.client}</td>
                                    <td className="mono" style={{ textAlign: "right" }}>{fmt(p.salary)}</td>
                                    <td>
                                        {p.salary ? (
                                            <span className="fn-pill paid">Оплачено</span>
                                        ) : (
                                            <span style={{ color: "var(--fn-text-soft)" }}>ещё не оплачено</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderExtra = () => {
        if (view === "list") return null;

        if (view === "tours") return <Breakdown title="По турам" entries={sortedEntries(groupBy(filtered, (o) => o.route))} onPick={setTour} />;
        if (view === "clients") return <Breakdown title="По заказчикам / партнёрам" entries={sortedEntries(groupBy(filtered, (o) => o.client))} onPick={setClient} />;
        if (view === "operators") return <Breakdown title="По операторам" entries={sortedEntries(groupBy(filtered, (o) => o.operator))} onPick={setOperator} />;

        if (view === "vehicles") {
            const map = {};
            filtered.forEach((o) => {
                (o.vehicleNames || [o.vehicle]).forEach((v) => {
                    if (!v || v === "—") return;
                    if (!map[v]) map[v] = { count: 0, sum: 0 };
                    map[v].count++;
                    map[v].sum += o.sum;
                });
            });
            return <Breakdown title="По машинам" entries={sortedEntries(map)} onPick={setVehicle} />;
        }

        if (view === "date") {
            const byDate = {};
            filtered.forEach((o) => {
                if (!byDate[o.date]) byDate[o.date] = { count: 0, sum: 0, display: o.displayDate };
                byDate[o.date].count++;
                byDate[o.date].sum += o.sum;
            });
            const entries = Object.entries(byDate).sort((a, b) => (a[0] < b[0] ? -1 : 1));
            return (
                <div className="rep-extra">
                    <div className="fn-section-label">По дням</div>
                    {entries.length ? (
                        entries.map(([iso, d]) => (
                            <div key={iso} className="rep-row" onClick={() => { setDateFrom(iso); setDateTo(iso); }}>
                                <span>{d.display}<span className="rep-count">×{d.count}</span></span>
                                <span className="rep-sum">${fmt(d.sum)}</span>
                            </div>
                        ))
                    ) : (
                        <div className="fn-empty">Нет данных за период</div>
                    )}
                </div>
            );
        }

        if (view === "cafe") {
            const map = {};
            filtered.forEach((o) => {
                (o.cafeAssignments || []).forEach((a) => {
                    if (!map[a.name]) map[a.name] = { count: 0, sum: 0, people: 0 };
                    map[a.name].count++;
                    map[a.name].sum += a.salary || 0;
                    map[a.name].people += a.count || 0;
                });
            });
            const entries = Object.entries(map).sort((a, b) => b[1].sum - a[1].sum);
            return (
                <div className="rep-extra">
                    <div className="fn-section-label">По кафе — сколько раз и на сколько человек кормили</div>
                    {entries.length ? (
                        entries.map(([name, d]) => (
                            <div key={name} className="rep-row" onClick={() => setCafe(name)}>
                                <span>{name}<span className="rep-count">×{d.count} заездов, {d.people} чел.</span></span>
                                <span className="rep-sum">{fmt(d.sum)} UZS</span>
                            </div>
                        ))
                    ) : (
                        <div className="fn-empty">Нет данных за период</div>
                    )}
                    {cafe !== "Все" && (
                        <div style={{ marginTop: 16, borderTop: "1px solid var(--fn-border)", paddingTop: 16 }}>
                            <div className="fn-section-label">{cafe} — история за период</div>
                            <div className="rep-table-wrap">
                                <table className="rep-table">
                                    <thead>
                                        <tr><th>Дата</th><th>Тур</th><th>Заказчик</th><th>Чел.</th><th style={{ textAlign: "right" }}>Сумма, UZS</th><th>Выплата</th></tr>
                                    </thead>
                                    <tbody>
                                        {filtered.filter((o) => (o.cafeNames || []).includes(cafe)).map((o) => {
                                            const a = (o.cafeAssignments || []).find((x) => x.name === cafe);
                                            if (!a) return null;
                                            return (
                                                <tr key={o.id}>
                                                    <td>{o.displayDate}</td>
                                                    <td>{o.route}</td>
                                                    <td>{o.client}</td>
                                                    <td className="mono">{a.count}</td>
                                                    <td className="mono" style={{ textAlign: "right" }}>{fmt(a.salary)}</td>
                                                    <td>
                                                        {a.salary ? (
                                                            <span className="fn-pill paid">Оплачено</span>
                                                        ) : (
                                                            <span style={{ color: "var(--fn-text-soft)" }}>ещё не оплачено</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        if (view === "guides" || view === "drivers") {
            const role = view === "guides" ? "guide" : "driver";
            const listField = role === "guide" ? "guides" : "drivers";
            const setSel = role === "guide" ? setGuide : setDriver;
            const selected = role === "guide" ? guide : driver;

            const map = {};
            filtered.forEach((o) => {
                (o[listField] || []).forEach((p) => {
                    if (!map[p.name]) map[p.name] = { trips: 0, salary: 0 };
                    map[p.name].trips++;
                    map[p.name].salary += p.salary || 0;
                });
            });
            const entries = Object.entries(map).sort((a, b) => b[1].salary - a[1].salary);

            return (
                <div className="rep-extra">
                    <div className="fn-section-label">
                        {role === "guide" ? "По гидам" : "По водителям"} — сколько раз вышли и сколько выплачено
                    </div>
                    {entries.length ? (
                        entries.map(([name, d]) => (
                            <div key={name} className="rep-row" onClick={() => setSel(name)}>
                                <span>{name}<span className="rep-count">×{d.trips} раз</span></span>
                                <span className="rep-sum">{fmt(d.salary)} UZS</span>
                            </div>
                        ))
                    ) : (
                        <div className="fn-empty">Нет данных за период</div>
                    )}
                    {renderPersonHistory(role, listField, selected)}
                </div>
            );
        }

        return null;
    };

    return (
        <div className="finance rep-page">
            <div className="fn-topbar">
                <div className="fn-pagetitle">{reportTab === "master" ? "Сводный отчёт" : "Прочие отчеты"}</div>
                <button
                    className="rep-download"
                    onClick={() => (reportTab === "master" ? downloadExcel() : miscDownloadRef.current && miscDownloadRef.current())}
                >
                    ⬇ Скачать в Excel
                </button>
            </div>

            <div className="fn-tabs">
                <div className={`fn-tab${reportTab === "master" ? " active" : ""}`} onClick={() => setReportTab("master")}>
                    Сводный отчёт
                </div>
                <div className={`fn-tab${reportTab === "misc" ? " active" : ""}`} onClick={() => setReportTab("misc")}>
                    Прочие отчеты
                </div>
            </div>

            {reportTab === "master" ? (
                <>


                    <div className="rep-filters">
                        <div className="rep-filters-row">
                            <div className="fn-field">
                                <label>Период — от</label>
                                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                            </div>
                            <div className="fn-field">
                                <label>Период — до</label>
                                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                            </div>
                            <div className="fn-field">
                                <label>Поиск</label>
                                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Имя, тур, машина..." />
                            </div>
                            <div className="fn-field">
                                <label>Тип заказа</label>
                                <select value={ctype} onChange={(e) => setCtype(e.target.value)}>
                                    {ORDER_TYPE_OPTIONS.map((o) => <option key={o}>{o}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="rep-filters-row">
                            <div className="fn-field">
                                <label>Заказчик</label>
                                <select value={client} onChange={(e) => setClient(e.target.value)}>
                                    <option>Все</option>
                                    {options.clients.map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="fn-field">
                                <label>Тур (маршрут)</label>
                                <select value={tour} onChange={(e) => setTour(e.target.value)}>
                                    <option>Все</option>
                                    {options.tours.map((t) => <option key={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className="fn-field">
                                <label>Оператор</label>
                                <select value={operator} onChange={(e) => setOperator(e.target.value)}>
                                    <option>Все</option>
                                    {options.operators.map((o) => <option key={o}>{o}</option>)}
                                </select>
                            </div>
                            <div className="fn-field">
                                <label>Гид</label>
                                <select value={guide} onChange={(e) => setGuide(e.target.value)}>
                                    <option>Все</option>
                                    {options.guides.map((g) => <option key={g}>{g}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="rep-filters-row" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                            <div className="fn-field">
                                <label>Машина</label>
                                <select value={vehicle} onChange={(e) => setVehicle(e.target.value)}>
                                    <option>Все</option>
                                    {options.vehicles.map((v) => <option key={v}>{v}</option>)}
                                </select>
                            </div>
                            <div className="fn-field">
                                <label>Водитель</label>
                                <select value={driver} onChange={(e) => setDriver(e.target.value)}>
                                    <option>Все</option>
                                    {options.drivers.map((d) => <option key={d}>{d}</option>)}
                                </select>
                            </div>
                            <div className="fn-field">
                                <label>Кафе</label>
                                <select value={cafe} onChange={(e) => setCafe(e.target.value)}>
                                    <option>Все</option>
                                    {options.cafes.map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="rep-layout">
                        <div className="rep-menu">
                            {VIEWS.map((v) => (
                                <div key={v.key} className={`rep-menu-item${view === v.key ? " active" : ""}`} onClick={() => setView(v.key)}>
                                    {v.label}
                                </div>
                            ))}
                        </div>

                        <div>
                            <div className="fn-stats" style={{ display: "flex", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
                                <div className="fn-stat"><div className="fn-lbl">Заказов</div><div className="fn-val">{filtered.length}</div></div>
                                <div className="fn-stat"><div className="fn-lbl">Сумма</div><div className="fn-val">${fmt(total)}</div></div>
                                <div className="fn-stat paid"><div className="fn-lbl">Оплачено заказов</div><div className="fn-val">{paidCount} / {filtered.length}</div></div>
                            </div>

                            {renderExtra()}

                            <div className="rep-table-wrap">
                                <table className="rep-table">
                                    <thead>
                                        <tr>
                                            <th>№ заказа</th><th>Дата</th><th>Оператор</th><th>Маршрут</th><th>Заказчик</th>
                                            <th>Транспорт</th><th>Машина</th><th>Водитель</th><th>Гид</th><th>Кафе</th>
                                            <th>Тип оплаты</th><th style={{ textAlign: "right" }}>Сумма</th><th>Статус</th><th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr><td colSpan={14} className="fn-empty">Загрузка…</td></tr>
                                        ) : filtered.length ? (
                                            filtered.map((o) => (
                                                <tr key={o.id}>
                                                    <td className="mono">{o.id}</td>
                                                    <td>{o.displayDate}</td>
                                                    <td>{o.operator}</td>
                                                    <td>{o.route}</td>
                                                    <td>{o.client}</td>
                                                    <td>{o.transport}</td>
                                                    <td>{o.vehicle}</td>
                                                    <td>{o.driver}</td>
                                                    <td>{o.guide}</td>
                                                    <td>{o.cafe}</td>
                                                    <td>{PAYMENT_LABELS[o.payment] || o.payment}</td>
                                                    <td className="mono" style={{ textAlign: "right" }}>${fmt(o.sum)}</td>
                                                    <td><span className={`fn-pill ${statusPillClass(o.statusKey)}`}>{o.status}</span></td>
                                                    <td>
                                                        <button className="rep-btn-detail" onClick={() => setDetailOrder(o)}>Подробно</button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr><td colSpan={14} className="fn-empty">Нет заказов по выбранным фильтрам</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <OrderDetailModal order={detailOrder} onClose={() => setDetailOrder(null)} />
                </>
            ) : (
                <MiscReport API_URL={API_URL} registerDownload={(fn) => { miscDownloadRef.current = fn; }} />
            )}
        </div>
    );
};

export default Reports;